import { useEffect } from "react";

export const useWizardNavBlocker = (isModal: boolean) => {
  useEffect(() => {
    const MARKER = "__wizBlock";

    if (isModal) {
      // Modal: mark the current history entry in-place with replaceState so no
      // extra entry is added. The URL never changes while the modal is open, so
      // any popstate that lands on an unmarked entry is back navigation.
      // On unmount the original state is restored — no go(-1) needed.
      const originalState = window.history.state as Record<
        string,
        unknown
      > | null;
      window.history.replaceState(
        { ...(originalState ?? {}), [MARKER]: true },
        "",
        window.location.href,
      );

      // isForwarding guards against the popstate fired by our own go(1) call
      // triggering a second go(1) loop. It does not need to handle user-initiated
      // forward navigation because go(1) always restores the user to the most
      // recent history entry — the browser forward button is therefore never
      // enabled while this hook is active.
      let isForwarding = false;
      const handlePopstate = (event: PopStateEvent) => {
        if (isForwarding) {
          isForwarding = false;
          return;
        }
        const state = event.state as Record<string, unknown> | null;
        if (!state?.[MARKER]) {
          isForwarding = true;
          window.history.go(1);
        }
      };

      window.addEventListener("popstate", handlePopstate);
      return () => {
        window.removeEventListener("popstate", handlePopstate);
        window.history.replaceState(originalState, "", window.location.href);
      };
    }

    // Non-modal: push a sentinel entry. The marker guard ensures only one
    // sentinel is added even under React StrictMode's double-invocation
    // (effect mount → cleanup → mount), since the sentinel pushed in the first
    // mount is still in history.state when the second mount runs.
    const currentState = window.history.state as Record<string, unknown> | null;
    if (!currentState?.[MARKER]) {
      window.history.pushState({ [MARKER]: true }, "", window.location.href);
    }

    let isForwarding = false;
    const handlePopstate = () => {
      if (isForwarding) {
        isForwarding = false;
        return;
      }
      isForwarding = true;
      window.history.go(1);
    };

    window.addEventListener("popstate", handlePopstate);
    return () => window.removeEventListener("popstate", handlePopstate);
  }, [isModal]);
};
